import { Link, useNavigate } from "react-router-dom";
import { LogOut, Languages, Building2, User as UserIcon, Shield } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useI18n } from "../lib/i18n";
import { logout } from "../lib/api";
import { LogoMark } from "./Logo";
import { NotificationBell } from "./NotificationBell";

export const Header = ({ user }) => {
  const navigate = useNavigate();
  const { t, lang, setLang } = useI18n();

  const handleLogout = async () => {
    await logout();
    navigate("/");
    window.location.reload();
  };

  return (
    <header
      data-testid="app-header"
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-background/70 border-b border-gold-soft"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center">
          <LogoMark />
        </Link>

        <nav className="hidden md:flex items-center gap-6 text-sm">
          <Link to="/" className="text-foreground/70 hover:text-gold transition-colors" data-testid="nav-home">{t("home")}</Link>
          {user && (
            <>
              <Link to="/foundation" className="text-foreground/70 hover:text-gold transition-colors" data-testid="nav-foundation">{t("foundation")}</Link>
              <Link to="/pipeline" className="text-foreground/70 hover:text-gold transition-colors" data-testid="nav-pipeline">{t("pipeline")}</Link>
              {user.role === "admin" && (
                <Link to="/admin" className="text-gold hover:text-foreground transition-colors" data-testid="nav-admin">Admin</Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "es" : "en")}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gold-soft text-foreground/70 hover:text-gold hover:border-gold-strong transition-colors font-medium"
            aria-label={lang === "en" ? "Cambiar a Español" : "Switch to English"}
          >
            <Languages className="w-3.5 h-3.5" strokeWidth={1.5} />
            {lang === "en" ? "Español" : "English"}
          </button>

          {user && <NotificationBell />}

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="account-menu-trigger" className="outline-none">
                <Avatar className="w-9 h-9 ring-1 ring-[rgba(232,184,75,0.5)]">
                  <AvatarImage src={user.profile_photo || ""} alt={user.display_name} />
                  <AvatarFallback className="bg-surface text-gold text-xs">
                    {(user.display_name || user.email || "?").slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-surface border-gold-soft">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-xs text-foreground/55">{t("signed_in_as")}</div>
                  <div className="text-sm font-medium truncate">{user.email}</div>
                  <div className="eyebrow mt-1">{t("your_role")}: {user.role}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-gold-soft" />
                {(user.role === "business" || user.role === "vendor" || user.role === "admin") && (() => {
                  const href = user.role === "business" ? "/business" : user.role === "vendor" ? "/vendor" : "/admin";
                  const Icon = user.role === "business" ? Building2 : user.role === "vendor" ? UserIcon : Shield;
                  return (
                    <DropdownMenuItem
                      asChild
                      data-testid="account-menu-my-dashboard"
                      className="cursor-pointer focus:bg-white/5"
                    >
                      <Link to={href} className="flex items-center" style={{ minHeight: 48 }}>
                        <Icon className="w-4 h-4 mr-2 text-gold" strokeWidth={1.5} />
                        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}>{t("menu_my_dashboard")}</span>
                      </Link>
                    </DropdownMenuItem>
                  );
                })()}
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="cursor-pointer focus:bg-white/5">
                  <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};
